import { router } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import type { View } from 'react-native'

import { ActionSheet } from '@/src/elements/action'
import { Button } from '@/src/elements/button/default'
import { Header, type THeaderProps } from '@/src/elements/header'
import type { TListboxItemProps } from '@/src/elements/listbox'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { RequiredRole } from '@/src/general/role-access'
import type { TImageSrc } from '@/src/general/utils/getImageUrl'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TGetCommunityApiData } from '../../../../../api/getCommunity.types'
import { COMMUNITY_LIFETIME } from '../../../../../models/community.constants'
import { CommunityShareModal } from '../../../../components/CommunityShareModal'
import { CommunityAdminButton } from '../../CommunityAdminButton'
import { useCommunityBackAction } from './useCommunityBackAction'

type TCommunityHeaderProps = {
  community?: TGetCommunityApiData
  onClickLeaveCommunity: VoidFunction
  redirect: string | undefined
  sharing: {
    picture?: TImageSrc
    trackingSource: TRACKING_SOURCE
    name?: string
  }
}

export const CommunityHeader = ({
  community,
  onClickLeaveCommunity,
  redirect,
  sharing,
}: TCommunityHeaderProps): ReactNode => {
  const { onBackPress } = useCommunityBackAction({ redirect })
  const { routes } = useRoutes()

  const getRightButtons = () => {
    const rightButtons: THeaderProps['rightButtons'] = []

    if (!community) return rightButtons

    const { id, hasJoined, isCreator, lifetime, slug } = community

    rightButtons.push({
      type: 'button',
      component: () => (
        <RequiredRole permission="canManageContent" permissionsMap={community}>
          <CommunityAdminButton {...community} testID={TEST_ID.COMMUNITY.OPTIONS.ADMIN_BUTTON} />
        </RequiredRole>
      ),
    })

    rightButtons.push({
      type: 'button',
      component: () =>
        sharing.picture && sharing.name ? (
          <CommunityShareModal
            key="community-share-modal"
            id={id}
            slug={slug}
            lifetime={lifetime}
            name={sharing.name}
            picture={sharing.picture}
            trackingSource={sharing.trackingSource}
          >
            {({ onShare, isLoading }) => (
              <Button
                testID={TEST_ID.COMMUNITY.SHARE.BUTTON}
                onPress={onShare}
                isIconOnly
                size="md"
                startIcon={ICON.SHARE}
                colors="primary"
                variant="faded"
                isLoading={isLoading}
              />
            )}
          </CommunityShareModal>
        ) : null,
    })

    if (!isCreator && hasJoined) {
      const grantedItems: TListboxItemProps[] = []

      // Only show notifications option if user has joined the community
      if (hasJoined) {
        grantedItems.push({
          onPress: () => router.navigate(routes.communityNotifications({ id, slug })),
          children: (
            <Typography colors="default">
              <FormattedMessage id="word.notifications.mails" />
            </Typography>
          ),
          startContent: <Icon name={ICON.BELL} size="sm" className="text-default-text-contrast-default" />,
          testID: TEST_ID.COMMUNITY.OPTIONS.NOTIFICATIONS,
        })
      }

      grantedItems.push({
        onPress: onClickLeaveCommunity,
        children: (
          <Typography colors="danger">
            {lifetime !== COMMUNITY_LIFETIME.EVERLASTING && (
              <FormattedMessage id="community.toolbar.stop" values={{ lifetime }} />
            )}
            {lifetime === COMMUNITY_LIFETIME.EVERLASTING && (
              <FormattedMessage id="community.leave-everlasting-community" values={{ lifetime }} />
            )}
          </Typography>
        ),
        startContent: <Icon name={ICON.ROAD_STOP} size="sm" className="text-danger-text-contrast-default" />,
        testID: TEST_ID.COMMUNITY.LEAVE,
      })

      rightButtons.push({
        type: 'button',
        component: () => (
          <ActionSheet items={grantedItems}>
            <Button
              testID={TEST_ID.COMMUNITY.OPTIONS.BUTTON}
              size="md"
              colors="primary"
              variant="faded"
              isIconOnly
              startIcon={ICON.ELLIPSIS_VERTICAL}
            />
          </ActionSheet>
        ),
      })
    }

    return rightButtons
  }

  const { t } = useTranslate()
  const { initializeRef } = useAccessibilityContext()
  const renderLeftButton = () => (
    <Button
      size="md"
      isIconOnly
      startIcon={ICON.ARROW_LEFT}
      onPress={onBackPress}
      colors="primary"
      variant="faded"
      testID={TEST_ID.NAVIGATION.BURGER_MENU(community ? `${community.id}` : 'unknown-community-id')}
      aria-label={t('app.how-to.open-menu')}
      ref={(ref: View) => {
        initializeRef(ref, 'menu')
      }}
    />
  )

  return (
    <Header
      leftButtons={[
        {
          type: 'button',
          component: renderLeftButton,
        },
      ]}
      rightButtons={getRightButtons()}
      className="bg-transparent z-50"
    />
  )
}
